import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { TimelineContext } from '../../../../../../contexts/TimelineContext'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import { ChevronUp } from 'react-bootstrap-icons'
import AssetListItem from './AssetListItem'
import { groupTimelineScenarioAssets } from '../../../../../../utils/groupTimelineScenarioAssets'
import { useReadOnly } from '../../../../../../contexts/ReadOnlyContext'

const groupShellClassName =
  'mb-3 overflow-hidden rounded-xl bg-rb-surface-muted px-3 py-3 dark:bg-rb-dark-mode-lighter/50'
const groupHeaderClassName =
  'flex w-full items-center justify-between px-1 text-left'
const groupToggleIconClassName =
  'ml-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-rb-widget-border bg-white text-rb-black shadow-sm dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white'

function ScenarioAssetInfluencerGroup({
  group,
  draggableIndexById,
  isOpen,
  onToggle,
  isActionUsedInTimeline,
}) {
  const readOnly = useReadOnly()

  return (
    <section className={groupShellClassName}>
      <button
        type="button"
        onClick={onToggle}
        className={groupHeaderClassName}
        aria-expanded={isOpen}
      >
        <h3 className="min-w-0 truncate text-sm font-semibold leading-6 text-rb-black dark:text-white">
          {group.name}
        </h3>
        <span className={groupToggleIconClassName}>
          <ChevronUp className={`text-xs transition-transform ${isOpen ? '' : 'rotate-180'}`} />
        </span>
      </button>

      {isOpen && (
        <div className="mt-3">
          {group.assets.map((asset) => {
            const isUsedInTimeline = isActionUsedInTimeline(asset.id)

            return (
              <Draggable
                key={`asset-${asset.id}`}
                draggableId={`action-${asset.id}`}
                index={draggableIndexById.get(String(asset.id))}
                isDragDisabled={readOnly || isUsedInTimeline}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{
                      ...provided.draggableProps.style,
                      opacity: snapshot.isDragging ? 0.7 : 1,
                      cursor: readOnly ? 'pointer' : isUsedInTimeline ? 'not-allowed' : 'grab',
                    }}
                  >
                    <AssetListItem
                      asset={asset}
                      isUsedInTimeline={isUsedInTimeline}
                      grouped
                    />
                  </div>
                )}
              </Draggable>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default observer(function ScenarioAssets() {
  const { actions, timelineState } = useContext(TimelineContext)
  const [openGroupKeys, setOpenGroupKeys] = useState({})
  const groupedActions = groupTimelineScenarioAssets(actions || [])
  const isGroupOpen = (groupKey) => openGroupKeys[groupKey] !== false

  const draggableIndexById = new Map()
  let nextDraggableIndex = 0
  groupedActions.forEach((group) => {
    if (!isGroupOpen(group.key)) return
    group.assets.forEach((asset) => {
      draggableIndexById.set(String(asset.id), nextDraggableIndex)
      nextDraggableIndex += 1
    })
  })

  const toggleGroup = (groupKey) => {
    setOpenGroupKeys((current) => ({
      ...current,
      [groupKey]: current[groupKey] === false,
    }))
  }

  const isActionUsedInTimeline = (actionId) => {
    const timelineItemsArray = Array.from(timelineState.timelineItems.values()).flat()
    return timelineItemsArray.some(item => String(item.id) === String(actionId))
  }

  return (
    <div className='mt-5 w-full'>
      <div className='flex items-center justify-between'>
        <h2 className='font-bold text-base dark:text-white'>Scenario Assets</h2>
        <span className='text-rb-lighter-grey dark:text-gray-400'>{actions?.length}</span>
      </div>

      <Droppable
        droppableId="scenario-assets"
        isDropDisabled={true}
        type="timelineItem"
      >
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="mt-3 min-h-[50px]"
          >
            {groupedActions.map((group) => (
              <ScenarioAssetInfluencerGroup
                key={group.key}
                group={group}
                draggableIndexById={draggableIndexById}
                isOpen={isGroupOpen(group.key)}
                onToggle={() => toggleGroup(group.key)}
                isActionUsedInTimeline={isActionUsedInTimeline}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  )
})
